import { S3Client } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { Upload } from '@aws-sdk/lib-storage';
import qs from 'qs';

import { UiToast } from 'src/components/UI/toasts';
import { deConfigurationSettingsSelector } from 'src/store/commonPart/selector';
import { store } from 'src/store/store';
import { SafeAnyType } from 'src/utils/safeAny';

declare global {
  interface Window {
    AWS: {
      config: SafeAnyType;
      S3: SafeAnyType;
      CognitoIdentityCredentials: SafeAnyType;
    };
  }
}

export const guid = (): string => {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

/** get converted url from s3 to cloudFront */
export const getSizedImageUrl = (filePath: string, customSizes?: { width: number; height: number }): string => {
  const {
    externalServices: {
      amazonS3: { cloudFrontUrl },
    },
  } = deConfigurationSettingsSelector(store.getState());

  let fileUri = filePath;

  let sizes = customSizes;

  if (filePath.startsWith('http')) {
    const temp = filePath.split('?')[0].split('/');
    let rootFolder = temp[temp.length - 2];

    // if there are double slashes after the root folder. Ex: rootFolder//file.jpg?width=960&height=640
    if (rootFolder === '') {
      rootFolder = `${temp[temp.length - 3]}/`;
    }

    const fileName = temp[temp.length - 1];

    fileUri = `${rootFolder}/${fileName}`;

    if (!customSizes) {
      const tempSizes = qs.parse(filePath.split('?')[1]);

      if (tempSizes?.width && tempSizes?.height) {
        sizes = tempSizes as unknown as { width: number; height: number };
      }
    }
  }

  return sizes ? `${cloudFrontUrl}${sizes.width}x${sizes.height}/${fileUri}` : `${cloudFrontUrl}${fileUri}`;
};

export const getPreviewImageUrl = (imageName: string): string => {
  const {
    externalServices: {
      amazonS3: { cloudFrontUrl },
    },
  } = deConfigurationSettingsSelector(store.getState());

  return `${cloudFrontUrl}preview/${imageName}`;
};

/** get unique file name for uploading */
export const getS3FileKey = (rootFolder: string, fileNamePrefix = '', fileExtension = 'jpg'): string => {
  const fileNameLocal = `${guid().split('-').join('')}.${fileExtension}`;

  let url = encodeURIComponent(rootFolder);

  if (!fileNamePrefix.startsWith('/')) {
    url += '/';
  }

  return url + fileNamePrefix + fileNameLocal;
};

export type S3UploadReturnType = {
  filePath: string;
  fullPath: string;
  fileName: string;
};

export type S3UploadReturnOptionalType = Partial<S3UploadReturnType>;

type S3UploadOptionsType = {
  fileNamePrefix?: string;
  fileName?: string;
  contentType?: string;
  fileExtension?: string;
};

/** upload image to aws */
export const S3Upload = (
  body: File | Blob,
  { fileNamePrefix, fileExtension, fileName = '', contentType = 'image/jpg' }: S3UploadOptionsType = {}
): Promise<S3UploadReturnType> => {
  try {
    const {
      externalServices: {
        amazonS3: { uploadedFilesBucketName, rootFolder, identityPoolId, region },
      },
    } = deConfigurationSettingsSelector(store.getState());

    const s3Client = new S3Client({
      region,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region },
        identityPoolId,
      }),
    });

    const uploadTask = new Upload({
      client: s3Client,
      params: {
        Bucket: uploadedFilesBucketName,
        ACL: 'public-read',
        Body: body,
        ContentType: contentType,
        Key: getS3FileKey(rootFolder, fileNamePrefix, fileExtension),
      },
      queueSize: 1,
      partSize: 20 * 1024 * 1024,
    });

    return uploadTask
      .done()
      .then((fileData): S3UploadReturnType => {
        if (!fileData.Key || !fileData.Location) {
          throw new Error('AWS S3 upload succeeded, but returned empty file paths.');
        }

        return {
          filePath: fileData.Key, // only file name (folder/file.ext)
          fullPath: fileData.Location, // full path (https://.../folder/file.ext)
          fileName,
        };
      })
      .catch((error) => {
        throw error;
      });

    /* const { AWS } = window;

    AWS.config.region = region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId,
    });
    AWS.config.correctClockSkew = true;

    const s3 = new AWS.S3({
      apiVersion: '2006-03-01',
    });

    return new Promise((resolve, reject) => {
      s3.upload(
        {
          Bucket: uploadedFilesBucketName,
          ACL: 'public-read',
          Body: body,
          ContentType: 'image/jpg',
          Key: getS3FileKey(rootFolder, fileNamePrefix),
        },
        {
          partSize: 20 * 1024 * 1024,
          queueSize: 1,
        },
        (e: Error, fileData: SafeAnyType) => {
          if (e) {
            reject(e);
          } else {
            resolve({
              filePath: fileData.Key, // only file name (folder/file.ext)
              fullPath: fileData.Location, // full path (https://.../folder/file.ext)
              fileName,
            });
          }
        }
      );
    }); */
  } catch (e) {
    UiToast.error('Unable to call AWS-SDK');

    return new Promise((_, reject) => {
      reject(e instanceof Error ? e : Error('Unable to call AWS-SDK'));
    });
  }
};
